import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import ExamTypeSection from './ExamTypeSection';
import MaternalStructureTabs from './MaternalStructureTabs';
import { useTechnologists } from '../hooks/useTechnologists';
import { getConcordance } from '../generators/utils/obDating';

const INITIAL_FIRST_TRIMESTER_DATA = {
  examType: 'First Trimester OB',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: 'Possible pregnancy',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  examTypeDetails: {
    transabdominal: false,
    transvaginal: true,
    transvaginalOnly: false
  },
  gestations: {
    numberAndType: 'Living single intrauterine pregnancy',
    type: '',
    gestationsList: [
      {
        gestationalSac: 'Normal',
        crl: '',
        yolkSac: '',
        fhr: '',
        subchorionic: 'None',
        subchronicSize: '',
        ultrasoundEGA: { weeks: '', days: '' },
        ultrasoundEDD: ''
      }
    ],
    uterus: 'Normal',
    priorCSection: false,
    uterusMasses: []
  },
  rightOvary: {
    status: ['Normal'],
    length: '',
    width: '',
    depth: '',
    other: '',
    masses: []
  },
  leftOvary: {
    status: ['Normal'],
    length: '',
    width: '',
    depth: '',
    other: '',
    masses: []
  },
  culDeSac: {
    fluidVolume: 'No free fluid',
    other: ''
  },
  gestationalAge: {
    clinicalEGA: { weeks: '', days: '' },
    clinicalEDD: '',
    lmp: ''
  },
  endometrium: {
    thickness: '',
    retainedProducts: false,
    otherFindings: ''
  },
  cervixLength: '',
  cervixNotVisualized: false,
  cervixFunneling: false
};

function FirstTrimesterOBForm({ token, onBack, facilityInfo }) {
  // Fetch dynamic technologists list
    const { technologists, defaultTech } = useTechnologists();
  // Use the custom hook for form management
  const {
    formData,
    updateField,
    updateMultipleFields,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm: hookResetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_FIRST_TRIMESTER_DATA, 
      technologist: defaultTech
    },
    'First Trimester OB', 
    {
      token, facilityInfo
    });

    // Update technologist field when defaultTech loads
    useEffect(() => {
      if (defaultTech && !formData.technologist) {
        updateField('technologist', defaultTech);
      }
    }, [defaultTech, formData.technologist, updateField]);

  // Fetus sub-tab (within Gestations) and outer section tab (Uterus / Endometrium / Cervix / Ovaries / Cul-de-sac)
  const [activeFetusTab, setActiveFetusTab] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  // Wrapper to handle form-specific cleanup on reset
  const resetForm = useCallback(() => {
    hookResetForm();
    setActiveFetusTab(0);
    setActiveTab(0);
  }, [hookResetForm]);

  // Handler functions using the hook's methods
  const handleGestationsChange = useCallback((field, value) => {
    const newGestations = {
      ...formData.gestations,
      [field]: value
    };
    
    // When switching to multiple gestations, ensure we have at least 2 fetuses
    if (field === 'numberAndType' && value === 'Living multiple intrauterine gestations') {
      if (formData.gestations.gestationsList.length < 2) {
        newGestations.gestationsList = [
          ...formData.gestations.gestationsList,
          {
            gestationalSac: 'Normal',
            crl: '',
            yolkSac: '',
            fhr: '',
            subchorionic: 'None',
            subchronicSize: '',
            ultrasoundEGA: { weeks: '', days: '' },
            ultrasoundEDD: ''
          }
        ];
      }
      setActiveFetusTab(0); // Reset to first tab
    }

    // When switching away from multiple gestations, keep only first gestation
    if (field === 'numberAndType' && value !== 'Living multiple intrauterine gestations' && formData.gestations.gestationsList.length > 1) {
      newGestations.gestationsList = [formData.gestations.gestationsList[0]];
      setActiveFetusTab(0);
    }
    
    // Auto-set FHR to "None Detected" for specific pregnancy types
    if (field === 'numberAndType' && 
        (value === 'Intrauterine pregnancy without cardiac activity' || 
         value === 'Gestational sac without visible embryo')) {
      newGestations.gestationsList = newGestations.gestationsList.map(gestation => ({
        ...gestation,
        fhr: 'None Detected'
      }));
    }
    
    // Auto-set gestational sac to "None visualized" for no visible pregnancy
    if (field === 'numberAndType' && value === 'No visible intrauterine pregnancy') {
      newGestations.gestationsList = newGestations.gestationsList.map(gestation => ({
        ...gestation,
        gestationalSac: 'None visualized'
      }));
    }
    
    updateField('gestations', newGestations);
  }, [formData.gestations, updateField]);

  const handleGestationItemChange = useCallback((index, field, value) => {
    const newGestationsList = formData.gestations.gestationsList.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateNestedField('gestations.gestationsList', newGestationsList);
  }, [formData.gestations.gestationsList, updateNestedField]);

  const addGestation = useCallback(() => {
    const newGestation = {
      gestationalSac: 'Normal',
      crl: '',
      yolkSac: '',
      fhr: '',
      subchorionic: 'None',
      subchronicSize: '',
      ultrasoundEGA: { weeks: '', days: '' },
      ultrasoundEDD: ''
    };
    updateNestedField('gestations.gestationsList', [...formData.gestations.gestationsList, newGestation]);
  }, [formData.gestations.gestationsList, updateNestedField]);

  const removeGestation = useCallback((index) => {
    if (formData.gestations.gestationsList.length > 1) {
      const newGestationsList = formData.gestations.gestationsList.filter((_, i) => i !== index);
      updateNestedField('gestations.gestationsList', newGestationsList);
    }
  }, [formData.gestations.gestationsList, updateNestedField]);

  const isMultipleGestations = formData.gestations.numberAndType === 'Living multiple intrauterine gestations';
  const isMultipleFetuses = formData.gestations.gestationsList.length > 1;

  // Calculate concordance for a specific gestation
  const calculateGestationConcordance = (gestation) => {
    const concordance = getConcordance(gestation.ultrasoundEGA, formData.gestationalAge.clinicalEGA);
    return concordance ? concordance.text : '';
  };

  const formatDate = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length === 0) return '';
    
    // Format as MM/DD/YYYY
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  const handleEGAChange = useCallback((egaField, subField, value) => {
    // Only allow numbers
    const numbers = value.replace(/\D/g, '');
    
    const newEGA = {
      ...formData.gestationalAge[egaField],
      [subField]: numbers
    };
    updateNestedField(`gestationalAge.${egaField}`, newEGA);
  }, [formData.gestationalAge, updateNestedField]);

  const handleGestationalAgeChange = useCallback((field, value) => {
    let formattedValue = value;
    
    // Apply formatting for date fields
    if (field === 'ultrasoundEDD' || field === 'clinicalEDD' || field === 'lmp') {
      formattedValue = formatDate(value);
    }
    
    updateNestedField(`gestationalAge.${field}`, formattedValue);
  }, [updateNestedField]);

  // Handle per-gestation EGA change
  const handleGestationEGAChange = useCallback((index, subField, value) => {
    const numbers = value.replace(/\D/g, '');
    
    const newGestationsList = formData.gestations.gestationsList.map((item, i) =>
      i === index ? {
        ...item,
        ultrasoundEGA: {
          ...item.ultrasoundEGA,
          [subField]: numbers
        }
      } : item
    );
    updateNestedField('gestations.gestationsList', newGestationsList);
  }, [formData.gestations.gestationsList, updateNestedField]);

  // Handle per-gestation EDD change
  const handleGestationEDDChange = useCallback((index, value) => {
    const formattedValue = formatDate(value);
    
    const newGestationsList = formData.gestations.gestationsList.map((item, i) =>
      i === index ? {
        ...item,
        ultrasoundEDD: formattedValue
      } : item
    );
    updateNestedField('gestations.gestationsList', newGestationsList);
  }, [formData.gestations.gestationsList, updateNestedField]);

  // The tabs use one shape on all OB forms; this form stores the uterus fields under gestations
  const { uterus, priorCSection, placentaScarDistance, uterusMasses } = formData.gestations;
  const maternal = {
    uterus,
    priorCSection,
    placentaScarDistance,
    uterusMasses,
    endometrium: formData.endometrium,
    cervixLength: formData.cervixLength,
    cervixNotVisualized: formData.cervixNotVisualized,
    cervixFunneling: formData.cervixFunneling,
    rightOvary: formData.rightOvary,
    leftOvary: formData.leftOvary,
    culDeSac: formData.culDeSac
  };

  const handleMaternalChange = (changes) => {
    const gestationFields = {};
    const otherFields = {};
    Object.entries(changes).forEach(([key, value]) => {
      (['uterus', 'priorCSection', 'placentaScarDistance', 'uterusMasses'].includes(key) ? gestationFields : otherFields)[key] = value;
    });
    updateMultipleFields({ ...otherFields, gestations: { ...formData.gestations, ...gestationFields } });
  };

  return (
    <div>
      <button onClick={onBack} className="back-btn">
        ← Back to Selection
      </button>

      <div className="form-container">
        <h2 style={{ textAlign: 'center', color: 'var(--color-primary)', marginBottom: '30px' }}>
          First Trimester OB Ultrasound Worksheet
        </h2>

        <form onSubmit={hookHandleSubmit}>
          {/* Status Messages */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
              <button 
                type="button"
                className="btn-close" 
                onClick={clearMessages}
                aria-label="Close"
              />
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              <strong>Success:</strong> {success}
              <button 
                type="button"
                className="btn-close" 
                onClick={clearMessages}
                aria-label="Close"
              />
            </div>
          )}

          {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
          <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

          {/* Exam Type */}
          <ExamTypeSection
            value={formData.examTypeDetails}
            onChange={(examTypeDetails) => updateField('examTypeDetails', examTypeDetails)}
          />

          {/* Uterus / Endometrium / Cervix / Right Ovary / Left Ovary / Cul-de-sac */}
          <MaternalStructureTabs
            maternal={maternal}
            onChange={handleMaternalChange}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            earlyPregnancy
          />

          {/* Gestations */}
          <div className="form-section">
            <h3>Gestations</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Number and Type:</label>
                <select
                  value={formData.gestations.numberAndType}
                  onChange={(e) => handleGestationsChange('numberAndType', e.target.value)}
                >
                  <option value="Living single intrauterine pregnancy">Living single intrauterine pregnancy</option>
                  <option value="Living multiple intrauterine gestations">Living multiple intrauterine gestations</option>
                  <option value="Intrauterine pregnancy without cardiac activity">Intrauterine pregnancy without cardiac activity</option>
                  <option value="Gestational sac without visible embryo">Gestational sac without visible embryo</option>
                  <option value="No visible intrauterine pregnancy">No visible intrauterine pregnancy</option>
                </select>
              </div>

              <div className="form-group">
                <label>Chorionicity:</label>
                <select
                  value={formData.gestations.type}
                  onChange={(e) => handleGestationsChange('type', e.target.value)}
                  disabled={!isMultipleGestations}
                  style={{
                    backgroundColor: !isMultipleGestations ? 'var(--color-disabled-bg)' : 'var(--color-input-bg)',
                    cursor: !isMultipleGestations ? 'not-allowed' : 'pointer',
                    color: !isMultipleGestations ? 'var(--color-disabled-text)' : 'var(--color-text)'
                  }}
                >
                  <option value="">Select type...</option>
                  <option>Dichorionic/Diamniotic</option>
                  <option>Monochorionic/Diamniotic</option>
                  <option>Monochorionic/Monoamniotic</option>
                  <option>Indeterminate</option>
                </select>
              </div>
            </div>

            {/* Tab Navigation */}
            {formData.gestations.gestationsList.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <div className="worksheet-tabs">
                  {formData.gestations.gestationsList.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveFetusTab(index)}
                      className={`worksheet-tab-btn${activeFetusTab === index ? ' active' : ''}`}
                    >
                      Fetus {index + 1}
                    </button>
                  ))}
                </div>

                {/* Active Tab Content */}
                {formData.gestations.gestationsList.map((gestation, index) => (
                  activeFetusTab === index && (
                    <div key={index} style={{ padding: '15px', border: '1px solid var(--color-border)', borderRadius: '5px', backgroundColor: 'var(--color-surface)' }}>
                      {formData.gestations.gestationsList.length > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              removeGestation(index);
                              if (activeFetusTab >= formData.gestations.gestationsList.length - 1) {
                                setActiveFetusTab(Math.max(0, formData.gestations.gestationsList.length - 2));
                              }
                            }}
                            className="remove-btn"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                          >
                            Remove Fetus {index + 1}
                          </button>
                        </div>
                      )}

                      <div className="tab-content-grid">
                      {/* Fetal measurements */}
                      <div>
                        <div className="form-group">
                          <label>Gestational Sac:</label>
                          <select
                            value={gestation.gestationalSac}
                            onChange={(e) => handleGestationItemChange(index, 'gestationalSac', e.target.value)}
                            style={{ width: '200px' }}
                          >
                            <option>Normal</option>
                            <option>Abnormal</option>
                            <option>None visualized</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>CRL (mm):</label>
                          <input
                            type="text"
                            value={gestation.crl}
                            onChange={(e) => handleGestationItemChange(index, 'crl', e.target.value)}
                            placeholder="mm"
                            style={{ width: '90px' }}
                          />
                        </div>

                        <div className="form-group">
                          <label>Yolk Sac (mm):</label>
                          <input
                            type="text"
                            value={gestation.yolkSac}
                            onChange={(e) => handleGestationItemChange(index, 'yolkSac', e.target.value)}
                            placeholder="mm"
                            style={{ width: '90px' }}
                          />
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '20px' }}>
                          {/* Wider than 3 digits: Number and Type can auto-fill "None Detected" */}
                          <div className="form-group">
                            <label>FHR (BPM):</label>
                            <input
                              type="text"
                              value={gestation.fhr}
                              onChange={(e) => handleGestationItemChange(index, 'fhr', e.target.value)}
                              placeholder="BPM"
                              style={{ width: '140px' }}
                            />
                          </div>

                          <div className="form-group">
                            <label>Subchorionic Hemorrhage:</label>
                            <select
                              value={gestation.subchorionic}
                              onChange={(e) => handleGestationItemChange(index, 'subchorionic', e.target.value)}
                              style={{ width: '190px' }}
                            >
                              <option>None</option>
                              <option>Present</option>
                            </select>
                          </div>

                          {gestation.subchorionic === 'Present' && (
                            <div className="form-group">
                              <label>Subchorionic Hemorrhage Size:</label>
                              <input
                                type="text"
                                value={gestation.subchronicSize}
                                onChange={(e) => handleGestationItemChange(index, 'subchronicSize', e.target.value)}
                                placeholder="Enter size"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Gestational Age — ultrasound dates are per fetus; clinical dates and LMP are shared */}
                      <div>
                        <h3 style={{ marginTop: 0 }}>Gestational Age</h3>

                        <div className="form-group">
                          <label>Ultrasound Dates:</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={gestation.ultrasoundEGA?.weeks || ''}
                              onChange={(e) => handleGestationEGAChange(index, 'weeks', e.target.value)}
                              placeholder="Weeks"
                              style={{ width: '80px' }}
                            />
                            <span>weeks</span>
                            <input
                              type="text"
                              value={gestation.ultrasoundEGA?.days || ''}
                              onChange={(e) => handleGestationEGAChange(index, 'days', e.target.value)}
                              placeholder="Days"
                              style={{ width: '80px' }}
                            />
                            <span style={{ marginRight: '20px' }}>days</span>
                            <span>EDD</span>
                            <input
                              type="text"
                              value={gestation.ultrasoundEDD || ''}
                              onChange={(e) => handleGestationEDDChange(index, e.target.value)}
                              placeholder="MM/DD/YYYY"
                              style={{ width: '120px' }}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>{isMultipleFetuses ? 'Clinical Dates (all fetuses):' : 'Clinical Dates:'}</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={formData.gestationalAge.clinicalEGA.weeks}
                              onChange={(e) => handleEGAChange('clinicalEGA', 'weeks', e.target.value)}
                              placeholder="Weeks"
                              style={{ width: '80px' }}
                            />
                            <span>weeks</span>
                            <input
                              type="text"
                              value={formData.gestationalAge.clinicalEGA.days}
                              onChange={(e) => handleEGAChange('clinicalEGA', 'days', e.target.value)}
                              placeholder="Days"
                              style={{ width: '80px' }}
                            />
                            <span style={{ marginRight: '20px' }}>days</span>
                            <span>EDD</span>
                            <input
                              type="text"
                              value={formData.gestationalAge.clinicalEDD}
                              onChange={(e) => handleGestationalAgeChange('clinicalEDD', e.target.value)}
                              placeholder="MM/DD/YYYY"
                              style={{ width: '120px' }}
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>{isMultipleFetuses ? 'LMP (all fetuses):' : 'LMP:'}</label>
                          <input
                            type="text"
                            value={formData.gestationalAge.lmp}
                            onChange={(e) => handleGestationalAgeChange('lmp', e.target.value)}
                            placeholder="Type: 01152025 = 01/15/2025"
                            style={{ width: '220px' }}
                          />
                        </div>

                        {calculateGestationConcordance(gestation) && (
                          <div className="form-group">
                            <label>Concordance:</label>
                            <input
                              type="text"
                              value={calculateGestationConcordance(gestation)}
                              readOnly
                              style={{ backgroundColor: 'var(--color-surface)', cursor: 'default' }}
                            />
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Add Gestation Button - always show for multiple gestations */}
            {isMultipleGestations && (
              <button
                type="button"
                onClick={() => {
                  addGestation();
                  setActiveFetusTab(formData.gestations.gestationsList.length);
                }}
                className="add-mass-btn"
                style={{ marginTop: '20px', marginBottom: '20px' }}
              >
                + Add Gestation
              </button>
            )}

          </div>

          {/* Comments */}
          <div className="form-section">
            <h3>Additional Comments</h3>
            <div className="form-group">
              <textarea
                value={formData.comments}
                onChange={(e) => updateField('comments', e.target.value)}
                rows="4"
                placeholder="Enter any additional comments or findings..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handlePreview}
              disabled={isLoading}
              className="preview-btn"
            >
              Preview Report
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isLoading}
            >
              {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
            </button>
            <button 
              type="button"
              onClick={resetForm}
              disabled={isLoading}
              className="reset-btn"
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FirstTrimesterOBForm;
